import { useState, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { Icon } from '../../icons/Icon';
import { NetworkArtwork } from './NetworkArtwork';
import { useDsT } from '../../services/i18n';

export interface AuthPageProps {
  /** Logo / wordmark for the navy brand panel (`onDark` variant). */
  brand?: ReactNode;
  /** Logo / wordmark shown above the card on small screens, where the brand panel is hidden. */
  mobileBrand?: ReactNode;
  tagline?: ReactNode;
  /** A sentence under the tagline. */
  claim?: ReactNode;
  /** Bottom-left of the brand panel (legal line). */
  legal?: ReactNode;
  /** Hides the network artwork. */
  artwork?: boolean;
  title: ReactNode;
  description?: ReactNode;
  /** The form. */
  children?: ReactNode;
  /** Buttons under the form. */
  actions?: ReactNode;
  /** Technical code (an OIDC error) behind a disclosure. */
  technical?: string;
  technicalLabel?: string;
  /** Below the card (deployment facts, links). */
  footer?: ReactNode;
  className?: string;
}

/**
 * The pre-session screen: a navy brand panel with the wordmark, tagline and network artwork on the left,
 * the form card on the right. Below `lg` the panel is dropped and the `mobileBrand` sits above the card.
 */
export function AuthPage({
  brand,
  mobileBrand,
  tagline: taglineProp,
  claim,
  legal,
  artwork = true,
  title,
  description,
  children,
  actions,
  technical,
  technicalLabel: technicalLabelProp,
  footer,
  className,
}: AuthPageProps) {
  const t = useDsT();
  const tagline = taglineProp ?? t('ds.authPage.tagline', 'One platform. Every movement.');
  const technicalLabel = technicalLabelProp ?? t('ds.errorState.technical', 'Technical details');
  const [open, setOpen] = useState(false);
  return (
    <main className={cn('zo-auth-page grid min-h-dvh bg-app text-fg lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]', className)}>
      <section data-slot="brand-panel" aria-hidden className="relative hidden overflow-hidden bg-sidebar-gradient text-white lg:flex lg:flex-col lg:justify-between lg:p-10">
        {artwork ? (
          <div className="pointer-events-none absolute inset-0 text-white">
            <NetworkArtwork />
          </div>
        ) : null}
        <div className="relative">{brand}</div>
        <div className="relative max-w-md">
          <p className="m-0 type-display text-white">{tagline}</p>
          {claim ? <p className="m-0 mt-3 type-body text-white/70">{claim}</p> : null}
        </div>
        <div className="relative type-caption text-white/50">{legal}</div>
      </section>
      <section data-slot="form-panel" className="flex flex-col items-center justify-center p-4 md:p-8">
        {mobileBrand ? <div className="mb-6 w-full max-w-[420px] lg:hidden">{mobileBrand}</div> : null}
        <div data-slot="card" className="w-full max-w-[420px] rounded-xl border border-border bg-surface-1 p-6 shadow-2 md:p-8">
          <h1 className="m-0 type-page-title text-fg">{title}</h1>
          {description ? <p className="m-0 mt-2 type-body text-fg-muted">{description}</p> : null}
          {children ? <div className="mt-6 flex flex-col gap-4">{children}</div> : null}
          {actions ? <div className="mt-6 flex flex-col gap-3">{actions}</div> : null}
          {technical ? (
            <div className="mt-5 type-caption text-fg-subtle">
              <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                className="inline-flex min-h-6 items-center gap-1 rounded-sm text-fg-muted hover:text-fg focus-ring"
              >
                <Icon name="chevron-right" size={12} className={cn('transition-transform motion-fast', open && 'rotate-90')} />
                {technicalLabel}
              </button>
              {open ? <code className="mt-1.5 block break-all code text-fg-muted">{technical}</code> : null}
            </div>
          ) : null}
        </div>
        {footer ? <div className="mt-6 w-full max-w-[420px]">{footer}</div> : null}
      </section>
    </main>
  );
}
